import {
  Injectable,
  PLATFORM_ID,
  Signal,
  TransferState,
  computed,
  inject,
  makeStateKey,
  signal,
} from '@angular/core';
import { isPlatformServer } from '@angular/common';
import { Observable, of } from 'rxjs';
import { catchError, map, tap, timeout } from 'rxjs/operators';
import { Region } from '@models/Location/region.model';
import { RegionService } from '@services/http/region.service';
import { CountryToken } from '@app/core/env.tokens';
import { StaticDataService } from '@services/logic/static-data-service';

// Regions are country-specific (dk/nl); scope the TransferState key so DK and
// NL never share a transfer entry.
export const regionsStateKey = (country: string) =>
  makeStateKey<Region[]>(`regions:${country}`);

// SSR is on the critical render path for every page; bound the DB fetch so a
// dashboard slowdown can't stall unrelated page renders. Fall back silently.
const REGION_SSR_TIMEOUT_MS = 800;

@Injectable({ providedIn: 'root' })
export class RegionStore {
  private readonly regionService = inject(RegionService);
  private readonly staticData = inject(StaticDataService);
  private readonly transferState = inject(TransferState);
  private readonly isServer = isPlatformServer(inject(PLATFORM_ID));
  private readonly country = inject(CountryToken);
  private readonly stateKey = regionsStateKey(this.country);

  // Initial value = the static fallback, so reads are never empty before priming.
  private readonly _regions = signal<Region[]>(this.staticData.fallbackRegions);

  readonly regions: Signal<Region[]> = this._regions.asReadonly();
  readonly activeRegions: Signal<Region[]> = computed(() =>
    this._regions().filter((r) => r.isActive),
  );

  /**
   * Replace the list. Ignores empty/non-array input so a bad fetch never blanks
   * the picker. Merges i18n labels onto the rows (labels live in i18n, not the DB).
   */
  setRegions(rows: Region[]): void {
    if (!Array.isArray(rows) || rows.length === 0) return;
    const labels = this.staticData.regionLabels?.();
    const merged = labels
      ? rows.map((r) => ({ ...r, name: labels[r.regionURL] ?? r.name }))
      : rows;

    // DB row order isn't guaranteed; re-order to match the curated (non-alphabetical)
    // fallback order so the picker stays byte-identical regardless of DB fetch order.
    // Rows not present in the fallback list keep their relative order and sort last.
    const order = new Map(
      this.staticData.fallbackRegions.map((r, i) => [r.regionURL, i]),
    );
    const sorted = [...merged].sort(
      (a, b) =>
        (order.get(a.regionURL) ?? Number.MAX_SAFE_INTEGER) -
        (order.get(b.regionURL) ?? Number.MAX_SAFE_INTEGER),
    );

    this._regions.set(sorted);
  }

  findActiveByURL(regionURL: string): Region | undefined {
    return this.activeRegions().find((r) => r.regionURL === regionURL);
  }

  /**
   * Prime the store from the DB. On the browser, prefer a value handed off from
   * SSR via TransferState. Falls back silently to the static list on any error.
   *
   * The TransferState read/write here is a deliberate manual handoff (not
   * redundant with the global http transfer cache): it lets the browser
   * resolve `regions()` synchronously from the SSR-computed value instead of
   * re-issuing the request and waiting on it.
   */
  primeFromServer(): Observable<Region[]> {
    const transferred = this.transferState.get(
      this.stateKey,
      null as Region[] | null,
    );
    if (transferred && transferred.length > 0) {
      this.setRegions(transferred);
      // Emit the actually-stored (label-merged, curated-ordered) value, consistent
      // with the fetch path's `map(() => this._regions())` normalization.
      return of(this._regions());
    }

    const fetch$ = this.regionService.findAll();
    // SSR is on the critical render path for every page; bound it so a slow
    // DB doesn't stall unrelated pages. Browser fetch stays un-timed (it's
    // rare — TransferState usually hits — and we don't want to abort a
    // legitimate browser-side fetch).
    const bounded$ = this.isServer
      ? fetch$.pipe(timeout(REGION_SSR_TIMEOUT_MS))
      : fetch$;

    return bounded$.pipe(
      tap((rows) => {
        this.setRegions(rows);
        if (Array.isArray(rows) && rows.length > 0)
          this.transferState.set(this.stateKey, rows);
      }),
      map(() => this._regions()), // normalize: emit the actually-applied value (never the ignored raw rows)
      catchError(() => of(this._regions())), // keep fallback (also handles a timeout)
    );
  }
}
